<template>
    <a-modal
        :open="open"
        title="⚠️ 二次確認：調整學員完課狀態"
        width="560px"
        centered
        :z-index="2000"
        :confirm-loading="loading"
        ok-text="確認變更"
        cancel-text="取消"
        :ok-button-props="{
            type: 'primary',
            danger: targetStatus === 'absent' || targetStatus === 'cancelled'
        }"
        @ok="handleConfirm"
        @cancel="handleCancel"
    >
        <div class="completion-confirm-body py-2">
            <!-- 財務與訂單影響警示 -->
            <a-alert
                type="warning"
                show-icon
                class="completion-warning-alert mb-4 border-amber-300 bg-amber-50"
            >
                <template #message>
                    <span class="font-bold text-amber-800">
                        重要權益與財務提示
                    </span>
                </template>
                <template #description>
                    <p class="text-amber-900 text-xs mt-1 leading-relaxed">
                        調整完課狀態可能會連動影響
                        <b>【訂單狀態】</b>
                        以及
                        <b>【財務 / 冰火戰場積分計分認列】</b>
                        。
                        <br />
                        請再次確認下方活動場次與受影響學員明細，確定後才執行變更。
                    </p>
                </template>
            </a-alert>

            <!-- 活動與場次資訊 -->
            <div
                class="completion-info-box p-3 rounded-lg border border-purple-200 bg-purple-50/60 mb-4"
            >
                <div class="flex items-center justify-between text-sm mb-1.5">
                    <span class="text-gray-500 font-medium">活動 / 場次：</span>
                    <span class="font-bold text-purple-900">
                        {{ displaySessionText }}
                    </span>
                </div>
                <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500 font-medium font-medium">
                        即將變更為：
                    </span>
                    <a-tag
                        :color="getStatusTagColor(targetStatus)"
                        class="font-bold px-2 py-0.5 text-sm m-0"
                    >
                        {{ targetStatusLabel || getStatusLabel(targetStatus) }}
                    </a-tag>
                </div>
            </div>

            <!-- 學員與狀態轉變對比清單 -->
            <div class="completion-attendees-section">
                <div
                    class="text-xs font-semibold text-gray-600 mb-2 flex justify-between items-center"
                >
                    <span>
                        即將調整狀態的學員名單 (共 {{ items.length }} 人)：
                    </span>
                </div>
                <div
                    class="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white space-y-1.5"
                >
                    <div
                        v-for="item in items"
                        :key="item.id"
                        class="flex items-center justify-between p-2 rounded bg-gray-50 hover:bg-purple-50/50 transition-colors text-xs"
                    >
                        <div class="flex items-center gap-2">
                            <span class="font-bold text-gray-800">
                                {{ item.userName || item.user_name || '學員' }}
                            </span>
                            <span
                                v-if="item.userId || item.user_id"
                                class="text-gray-400 text-[11px]"
                            >
                                (ID: {{ item.userId || item.user_id }})
                            </span>
                            <span
                                v-if="item.sessionName && items.length > 1"
                                class="text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded text-[11px]"
                            >
                                {{ item.sessionName }}
                            </span>
                        </div>
                        <div class="flex items-center gap-1.5">
                            <a-tag
                                :color="getStatusTagColor(item.currentStatus)"
                                size="small"
                                class="m-0"
                            >
                                {{
                                    item.currentStatusLabel ||
                                    getStatusLabel(item.currentStatus)
                                }}
                            </a-tag>
                            <span class="text-gray-400 font-bold">➔</span>
                            <a-tag
                                :color="getStatusTagColor(targetStatus)"
                                size="small"
                                class="m-0"
                            >
                                {{
                                    targetStatusLabel ||
                                    getStatusLabel(targetStatus)
                                }}
                            </a-tag>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: false
    },
    activityName: {
        type: String,
        default: ''
    },
    sessionName: {
        type: String,
        default: ''
    },
    targetStatus: {
        type: String,
        default: ''
    },
    targetStatusLabel: {
        type: String,
        default: ''
    },
    items: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['confirm', 'cancel', 'update:open']);

const displaySessionText = computed(() => {
    if (props.activityName && props.sessionName) {
        return `${props.activityName} - ${props.sessionName}`;
    }
    if (props.sessionName) {
        return props.sessionName;
    }
    if (props.activityName) {
        return props.activityName;
    }
    if (
        props.items &&
        props.items.length === 1 &&
        props.items[0]?.sessionName
    ) {
        return props.items[0].sessionName;
    }
    return props.items && props.items.length > 1
        ? '多項 / 跨場次'
        : '未指定場次';
});

const STATUS_MAP = {
    none: { label: '準備上課', tag: 'blue' },
    completed: { label: '已完課', tag: 'purple' },
    absent: { label: '未完課', tag: 'orange' },
    cancelled: { label: '已取消', tag: 'red' }
};

const getStatusLabel = (status) =>
    STATUS_MAP[status]?.label ?? status ?? '未定';
const getStatusTagColor = (status) => STATUS_MAP[status]?.tag ?? 'default';

const handleConfirm = () => {
    emit('confirm');
};

const handleCancel = () => {
    emit('cancel');
    emit('update:open', false);
};
</script>

<style scoped>
.completion-confirm-body :deep(.ant-alert-message) {
    font-size: 13px;
}
</style>
